import React from 'react';
import './TournamentPage.css';
import TournamentChampionCelebration from './TournamentChampionCelebration';

export interface ReadyCheckData {
  matchId: number;
  tournamentId: number;
  tournamentName: string;
  opponentName: string;
  round: number;
  timeoutSeconds: number;
}

export interface GlobalTournamentOverlaysProps {
  tournamentBanner: { name: string; tournamentId: number } | null;
  onBannerGoToTournament: () => void;
  onBannerDismiss: () => void;
  tournamentReadyCheck: ReadyCheckData | null;
  tournamentRCCountdown: number;
  tournamentRCAccepted: boolean;
  /** True after socket `tournament:peerReady` for this match — opponent clicked Accept. */
  opponentPeerReady: boolean;
  myUsername: string | null | undefined;
  onAcceptTournamentReady: () => void;
  championCelebration?: { tournamentName: string } | null;
  onDismissChampionCelebration?: () => void;
}

/**
 * Tournament "started" strip + ready-check modals must render on every app route
 * (same z-index stack as main game) so players never miss them.
 */
const GlobalTournamentOverlays: React.FC<GlobalTournamentOverlaysProps> = ({
  tournamentBanner,
  onBannerGoToTournament,
  onBannerDismiss,
  tournamentReadyCheck,
  tournamentRCCountdown,
  tournamentRCAccepted,
  opponentPeerReady,
  myUsername,
  onAcceptTournamentReady,
  championCelebration = null,
  onDismissChampionCelebration
}) => (
  <>
    {tournamentBanner && (
      <div
        className="trn-banner"
        style={{
          position: 'fixed',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a3a5c',
          color: '#fff',
          padding: '14px 24px',
          borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          zIndex: 19000,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          maxWidth: 480
        }}
      >
        <span>
          🏆 <strong>{tournamentBanner.name}</strong> has started! You are registered.
        </span>
        <button
          type="button"
          onClick={onBannerGoToTournament}
          style={{
            background: '#4a9eff',
            border: 'none',
            color: '#fff',
            padding: '6px 14px',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}
        >
          Go to Tournament
        </button>
        <button
          type="button"
          onClick={onBannerDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#aaa',
            fontSize: 18,
            cursor: 'pointer',
            lineHeight: 1,
            padding: '0 4px'
          }}
        >
          ✕
        </button>
      </div>
    )}

    {tournamentReadyCheck && !tournamentRCAccepted && (
      <div className="tp-ready-overlay" style={{ zIndex: 20000 }}>
        <div className="tp-ready-modal">
          <div className="tp-ready-title">🏆 {tournamentReadyCheck.tournamentName}</div>
          <div className="tp-ready-round">Round {tournamentReadyCheck.round}</div>
          <div className="tp-ready-vs">
            <span className="tp-ready-you">{myUsername}</span>
            <span className="tp-ready-vstext">vs</span>
            <span className="tp-ready-opp">{tournamentReadyCheck.opponentName}</span>
          </div>
          <div className="tp-ready-message">
            {opponentPeerReady
              ? `${tournamentReadyCheck.opponentName} has accepted — click Accept to join the match.`
              : 'Your match is ready. Click Accept to begin!'}
          </div>
          <div className="tp-ready-countdown">
            <svg className="tp-countdown-ring" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#2a3650" strokeWidth="6" />
              <circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke={tournamentRCCountdown <= 10 ? '#e74c3c' : '#27ae60'}
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 34}`}
                strokeDashoffset={`${2 * Math.PI * 34 * (1 - tournamentRCCountdown / tournamentReadyCheck.timeoutSeconds)}`}
                strokeLinecap="round"
                transform="rotate(-90 40 40)"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <span className="tp-countdown-num" style={{ color: tournamentRCCountdown <= 10 ? '#e74c3c' : '#fff' }}>
              {tournamentRCCountdown}
            </span>
          </div>
          <button type="button" className="tp-ready-btn" onClick={onAcceptTournamentReady}>
            Accept Match
          </button>
          <div className="tp-ready-warn">If you don&apos;t accept in time, your opponent wins automatically.</div>
        </div>
      </div>
    )}
    {tournamentReadyCheck && tournamentRCAccepted && (
      <div className="tp-ready-overlay" style={{ zIndex: 20000 }}>
        <div className="tp-ready-modal">
          <div className="tp-ready-title">✅ You&apos;re Ready!</div>
          <div className="tp-ready-round">Round {tournamentReadyCheck.round}</div>
          <div className="tp-ready-vs">
            <span className="tp-ready-you">{myUsername}</span>
            <span className="tp-ready-vstext">vs</span>
            <span className="tp-ready-opp">{tournamentReadyCheck.opponentName}</span>
          </div>
          <div className="tp-ready-message">
            {opponentPeerReady
              ? `${tournamentReadyCheck.opponentName} accepted — starting the match...`
              : `Waiting for ${tournamentReadyCheck.opponentName} to accept...`}
          </div>
          <div className="tp-ready-countdown">
            <svg className="tp-countdown-ring" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#2a3650" strokeWidth="6" />
              <circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke={tournamentRCCountdown <= 10 ? '#e74c3c' : '#27ae60'}
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 34}`}
                strokeDashoffset={`${2 * Math.PI * 34 * (1 - tournamentRCCountdown / tournamentReadyCheck.timeoutSeconds)}`}
                strokeLinecap="round"
                transform="rotate(-90 40 40)"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <span className="tp-countdown-num" style={{ color: tournamentRCCountdown <= 10 ? '#e74c3c' : '#fff' }}>
              {tournamentRCCountdown}
            </span>
          </div>
          <div className="tp-ready-warn">
            {tournamentRCCountdown <= 10
              ? 'Opponent is running out of time...'
              : "If opponent doesn't accept in time, you win automatically."}
          </div>
        </div>
      </div>
    )}

    {championCelebration && onDismissChampionCelebration && (
      <TournamentChampionCelebration
        tournamentName={championCelebration.tournamentName}
        onDismiss={onDismissChampionCelebration}
      />
    )}
  </>
);

export default GlobalTournamentOverlays;
